import { MaterialPluginBase, ShaderLanguage } from '@babylonjs/core';
import type { Material } from '@babylonjs/core';

export interface WaterDistancePluginParams {
  /**
   * Distance from the camera (in metres) at which the emissive (sparkle) layer is fully
   * suppressed. Fragments closer than this see zero emissive contribution.
   */
  emissiveFadeNear: number;
  /**
   * Distance at which the emissive layer reaches full strength. Smoothstep crossfade
   * between the two values.
   */
  emissiveFadeFar: number;
}

/**
 * `PBRMaterial` plugin that fades the **emissive** layer with camera distance.
 *
 * Implementation notes:
 *
 *   - The fade constants (`fadeNear`, `fadeFar`) are baked **directly into the shader
 *     source** rather than passed as uniforms. This sidesteps Babylon's plugin-uniform
 *     wiring entirely — the previous incarnation used a UBO entry and on WebGPU the
 *     uniform appeared to never reach the shader (sparkles fired everywhere regardless
 *     of distance). With baked literals there's no binding step to go wrong; only the
 *     shader-injection path is needed.
 *
 *   - The hook is `CUSTOM_FRAGMENT_BEFORE_FINALCOLORCOMPOSITION`, which fires after
 *     PBR has finished assembling `finalEmissive` (texture sampled, intensity applied)
 *     and before `finalEmissive` is added into the output colour. Multiplying by a
 *     smoothstep distance factor scales the entire emissive contribution per-fragment.
 *
 *   - Dual-language: WGSL and GLSL snippets provided. WebGPU `PBRMaterial` uses native
 *     WGSL — the default plugin `isCompatible` only accepts GLSL, so we override.
 *
 *   - Tracing: constructor, `prepareDefines`, and `getCustomCode` all log once each so we
 *     can confirm in the browser console that each stage actually runs.
 */
export class WaterDistancePlugin extends MaterialPluginBase {
  isEnabled = true;
  emissiveFadeNear: number;
  emissiveFadeFar: number;
  /** One-shot log gates so the console doesn't spam every frame. */
  private _loggedPrepare = false;
  private _loggedCustomCode = false;

  constructor(material: Material, params: WaterDistancePluginParams) {
    // IMPORTANT: pass `addToPluginList = false` (5th arg). If we let the base constructor
    // auto-register, it synchronously calls `_addPlugin` → `getCustomCode` *before* this
    // subclass's constructor body runs, so `this.emissiveFadeNear/Far` are still
    // undefined when `getCustomCode` tries to read them and `.toFixed()` throws.
    //
    // We defer registration: set fields first, then push the plugin into the manager
    // ourselves. `_addPlugin` is marked `@internal` in Babylon's types but is the only
    // entry point for late-registration — it's been stable since this API exists.
    super(material, 'WaterDistance', 200, { WATER_DISTANCE_PLUGIN: false }, false, false);
    this.emissiveFadeNear = params.emissiveFadeNear;
    this.emissiveFadeFar = params.emissiveFadeFar;

    // Now that fields are set, register and activate. `_addPlugin` triggers the first
    // `getCustomCode` invocation with our literals correctly populated.
    const mgr = material.pluginManager as unknown as {
      _addPlugin(plugin: MaterialPluginBase): boolean;
    };
    mgr._addPlugin(this);
    this._enable(true);

    console.log(
      `[WaterDistancePlugin] constructor: attached to "${material.name}", fade ${params.emissiveFadeNear}..${params.emissiveFadeFar}m`,
    );
  }

  override isCompatible(shaderLanguage: ShaderLanguage): boolean {
    return shaderLanguage === ShaderLanguage.GLSL || shaderLanguage === ShaderLanguage.WGSL;
  }

  override getClassName(): string {
    return 'WaterDistancePlugin';
  }

  override prepareDefines(defines: Record<string, boolean>): void {
    defines.WATER_DISTANCE_PLUGIN = this.isEnabled;
    if (!this._loggedPrepare) {
      this._loggedPrepare = true;
      console.log('[WaterDistancePlugin] prepareDefines fired (define set true)');
    }
  }

  override getCustomCode(
    shaderType: string,
    shaderLanguage: ShaderLanguage = ShaderLanguage.GLSL,
  ): Record<string, string> | null {
    if (shaderType !== 'fragment') return null;

    if (!this._loggedCustomCode) {
      this._loggedCustomCode = true;
      console.log(
        `[WaterDistancePlugin] getCustomCode fired (language=${shaderLanguage === ShaderLanguage.WGSL ? 'WGSL' : 'GLSL'})`,
      );
    }

    const near = this.emissiveFadeNear.toFixed(2);
    const far = this.emissiveFadeFar.toFixed(2);

    if (shaderLanguage === ShaderLanguage.WGSL) {
      return {
        // WGSL: fragmentInputs.* for varyings, scene.* for scene uniforms, `finalEmissive`
        // is a local var declared in pbrBlockFinalUnlitComponents (just above this hook).
        CUSTOM_FRAGMENT_BEFORE_FINALCOLORCOMPOSITION: `
          let waterEDist: f32 = distance(fragmentInputs.vPositionW, scene.vEyePosition.xyz);
          let waterEFactor: f32 = smoothstep(${near}, ${far}, waterEDist);
          finalEmissive = finalEmissive * waterEFactor;
        `,
      };
    }

    // NOTE: identifier names MUST NOT start with `__` — GLSL ES 3.00 reserves
    // double-underscore prefixes as "possible future keywords" and rejects them as
    // undeclared on strict drivers (Angle WebGL2 in Chrome). With the old `__waterE*`
    // names the whole water material failed to compile and rendered invisibly.
    return {
      CUSTOM_FRAGMENT_BEFORE_FINALCOLORCOMPOSITION: `
        float waterEDist = distance(vPositionW, vEyePosition.xyz);
        float waterEFactor = smoothstep(${near}, ${far}, waterEDist);
        finalEmissive *= waterEFactor;
      `,
    };
  }
}
